import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { verifyResetToken, resetPassword } from '../services/forgotPasswordService'

const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No reset token provided')
        setVerifying(false)
        return
      }

      try {
        const result = await verifyResetToken(token)
        if (result.success) {
          setTokenValid(true)
          setEmail(result.email || '')
        } else {
          setError(result.message)
        }
      } catch (error: any) {
        setError(error.message)
      } finally {
        setVerifying(false)
      }
    }

    verifyToken()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    // Validation
    if (!newPassword) {
      setError('Please enter a new password')
      setLoading(false)
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      setLoading(false)
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    try {
      const result = await resetPassword({
        token: token!,
        newPassword
      })

      if (result.success) {
        setSuccess(true)
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      }
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToHome = () => {
    navigate('/')
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  if (verifying) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5>Verifying Reset Link...</h5>
            <p className="text-muted">Please wait while we verify your password reset link.</p>
          </div>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="text-danger mb-3">Invalid Reset Link</h5>
            <p className="text-muted mb-4">{error}</p>
            <div className="d-flex gap-2 justify-content-center">
              <button 
                className="btn btn-primary"
                onClick={handleBackToLogin}
              >
                Back to Login
              </button>
              <button 
                className="btn btn-outline-secondary"
                onClick={handleBackToHome}
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="text-success mb-3">Password Reset Successful!</h5>
            <p className="text-muted mb-4">
              Your password has been successfully reset. You can now log in with your new password.
            </p>
            <p className="small text-muted mb-4">
              You will be redirected to the login page in a few seconds...
            </p>
            <button 
              className="btn btn-primary"
              onClick={handleBackToLogin}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="card shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
        <div className="card-body p-4">
          <div className="text-center mb-4">
            <div className="mb-3">
              <i className="bi bi-shield-lock-fill text-primary" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <h4 className="card-title">Reset Your Password</h4>
            <p className="text-muted">
              Enter your new password for <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>
              <input
                type="password"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                id="newPassword"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                autoFocus
              />
              <div className="form-text">
                Password must be at least 8 characters long
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm New Password
              </label>
              <input
                type="password"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                id="confirmPassword"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              {error && (
                <div className="invalid-feedback">
                  {error}
                </div>
              )}
            </div>

            <button 
              type="submit" 
              className="btn btn-primary w-100 mb-3"
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Resetting Password...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Reset Password
                </>
              )}
            </button>

            <div className="d-flex justify-content-between">
              <button 
                type="button"
                className="btn btn-link text-decoration-none"
                onClick={handleBackToHome}
                disabled={loading}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Back to Home
              </button>
              <button 
                type="button"
                className="btn btn-link text-decoration-none"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                Back to Login
                <i className="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordForm
